import React, { useMemo } from 'react'
import { Button, Input } from '@surecompanies/core_components'
import { Select } from 'antd'
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons'
import isNil from 'lodash/isNil'
import { type UserListStats } from '../../api/dto/user-invitation.dto'
import { sureco } from '../shared/finance.styles'
import {
  FilterBar,
  FilterField,
  SearchInputWrap,
  SearchRow,
  SearchRowActions,
} from './UserManagement.styles'
import { buildScopeOptions, type UserScope } from './userDisplay'

interface UsersToolbarProps {
  search: string
  scope: UserScope
  /** Per-option counts; null until the first load lands. */
  stats: UserListStats | null
  /** True while the status funnel is narrowing the list. */
  isFunnelActive: boolean
  isExportDisabled: boolean
  onSearchChange: (value: string) => void
  onScopeChange: (value: UserScope) => void
  onExport: () => void
}

/** Search, the completeness filter, and the export the roster is read with. */
export const UsersToolbar = ({
  search,
  scope,
  stats,
  isFunnelActive,
  isExportDisabled,
  onSearchChange,
  onScopeChange,
  onExport,
}: UsersToolbarProps) => {
  // Counts ride along with the rows, so they are absent until the first load
  // lands and the labels stay bare rather than reading "(0)". They also count
  // the whole roster, which reads as a contradiction beside a table the status
  // funnel has already narrowed, so they stand down while it is in force.
  const scopeOptions = useMemo(
    () =>
      buildScopeOptions(stats).map(({ label, value, count }) => ({
        value,
        label: isNil(count) || isFunnelActive ? label : `${label} (${count})`,
      })),
    [stats, isFunnelActive]
  )

  return (
    <SearchRow>
      <SearchInputWrap>
        <Input
          prefix={<SearchOutlined style={{ color: sureco.fg3 }} />}
          placeholder="Search by email or name…"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          allowClear
        />
      </SearchInputWrap>
      <FilterBar>
        <FilterField>
          <Select<UserScope>
            style={{ width: '100%' }}
            value={scope}
            onChange={onScopeChange}
            options={scopeOptions}
            aria-label="Filter users by completeness"
          />
        </FilterField>
      </FilterBar>
      <SearchRowActions>
        <Button
          size="small"
          icon={<DownloadOutlined />}
          onClick={onExport}
          disabled={isExportDisabled}
        >
          Export
        </Button>
      </SearchRowActions>
    </SearchRow>
  )
}
